import './EditWebhook.scss';

import { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import NavHeading, { NavHeadingProps } from 'components/NavHeading/NavHeading';
import { NavHeadings } from 'constants/Constants';
import { useApiConnectionService } from 'customHooks/useApiConnectionService';
import { useModifyEntityService } from 'customHooks/useModifyEntityService';
import { useSingleEntity } from 'customHooks/useSingleEntity';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import { isEmptyObject } from 'functions/isEmptyObject';
import { ApiConnection } from 'models/ApiConnection';
import { ConnectionStatus } from 'models/ConnectionStatus';
import { DocumentStatusKey } from 'models/DocumentStatusKey';
import { Entities } from 'models/Entities';
import { LogLevel } from 'models/LogLevel';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Select, { MultiValue } from 'react-select';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface EditWebhookProps { }

const EditWebhook: FC<EditWebhookProps> = () => {
  const newEntryId = 'new';
  const navigate = useNavigate();
  const { id } = useParams();
  const connectionId: number | undefined = id !== newEntryId ? parseInt(id!, 10) : undefined;
  const {
    configRepo,
    projects,
    setIsProjectSelectable,
    selectedProjectId,
    setSelectedProjectIdAsync,
    notify,
    sendLog
  } = useContext(AppContext);
  const onTokenExpired = useTokenExpiredHandler();

  useEffect(() => {
    setIsProjectSelectable(false);
  }, []);

  const apiConnectionService = useApiConnectionService(configRepo!.PUBLISH_API_BASE_URI);

  const defaultConnection: ApiConnection = {
    apiUrl: '',
    name: '',
    apiKey: null,
    orderId: 0,
    conditions: [],
    projectId: selectedProjectId!,
    connectionStatus: ConnectionStatus.OK
  };
  const [cachedConnection, setCachedConnection] = useState<ApiConnection>(defaultConnection);

  const { createEntity: createConnection, updateEntity: updateConnection } =
    useModifyEntityService<ApiConnection>(
      configRepo!.PUBLISH_API_BASE_URI,
      Entities.ApiConnections,
      sendLog,
      onTokenExpired,
    );

  const [conditions, setConditions] = useState<Array<{ value: string, label: string }>>([]);

  const { entityRequest: connectionRequest } = useSingleEntity<ApiConnection>(
    configRepo!.PUBLISH_API_BASE_URI,
    Entities.ApiConnections,
    connectionId,
  );
  const loadedConnection: ApiConnection = connectionRequest.entity;
  useEffect(() => {
    const updatedCachedConnection = { ...loadedConnection };
    if (isEmptyObject(updatedCachedConnection)) {
      return;
    }
    setCachedConnection(updatedCachedConnection);
    const selectedConditions = updatedCachedConnection.conditions.map(c => ({ value: c as string, label: c as string }));
    setConditions(selectedConditions);
  }, [loadedConnection]);



  const onDocumentFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const updatedCachedConnection = { ...cachedConnection };
    (updatedCachedConnection as any)[event.target.name] = event.target.value;
    setCachedConnection(updatedCachedConnection);
  };

  const onSelectionChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    const updatedCachedConnection = { ...cachedConnection };
    (updatedCachedConnection as any)[event.target.name] = event.target.value;
    setCachedConnection(updatedCachedConnection);

    if (event.target.name === 'projectId') {
      await setSelectedProjectIdAsync(parseInt(event.target.value, 10));
      sendLog(`Updated project context for connection to ${event.target.value}`, LogLevel.info);
    }
  };

  const options: Array<{ value: string, label: string }> = [];
  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const key in DocumentStatusKey) {
    options.push({ value: key, label: key });
  }

  const logAndNotifyConnectionSaved = (updatedConnection: ApiConnection) => {
    sendLog(`Connection has been updated successfully, ${updatedConnection.id}`, LogLevel.info);
    notify('Connection has been updated successfully.', NotificationType.Success);
  };

  const logAndNotifyConnectionSaveFailure = (connectionForUpdate: ApiConnection) => {
    notify('Connection has NOT been saved successfully.', NotificationType.Error);
    sendLog(`Connection has NOT been updated successfully, ${connectionForUpdate.id}`, LogLevel.error, { connectionId: connectionForUpdate.id });
  };

  const onSave = async (): Promise<{ hasSavedSuccessfully: boolean, connection: ApiConnection }> => {
    let updatedConnection = { ...cachedConnection };
    let hasSavedSuccessfully = false;

    if (id === newEntryId) {
      const result = await createConnection(updatedConnection);
      if (result) {
        setCachedConnection(result);
        updatedConnection = result; // have to update current context since that's used downstream from here
        navigate(window.location.pathname.replace(newEntryId, result.id!.toString()), {
          replace: true,
        });
        hasSavedSuccessfully = true;
      } else {
        hasSavedSuccessfully = false;
      }
    } else {
      hasSavedSuccessfully = await updateConnection(updatedConnection);
    }

    return { hasSavedSuccessfully, connection: updatedConnection };
  };

  const testConnection = async (): Promise<boolean> => {
    let updatedConnection = { ...cachedConnection };

    if (!updatedConnection.apiUrl) {
      notify('Required property URL is missing.', NotificationType.Error);
      return false;
    }
    if (!updatedConnection.conditions || updatedConnection.conditions.length === 0) {
      notify('Required property Conditions is missing.', NotificationType.Error);
      return false;
    }

    const saveResult = await onSave();
    if (!saveResult.hasSavedSuccessfully) {
      notify('Failed to update connection prior to executing test.', NotificationType.Error);
      return false;
    }

    updatedConnection = { ...saveResult.connection };

    const testResult = await apiConnectionService.testConnection(saveResult.connection);
    if (testResult.isSuccessful) {
      notify("Connection test succeeded!", NotificationType.Success);
      updatedConnection.connectionStatus = ConnectionStatus.OK;
      updatedConnection.connectionErrorCode = undefined;
      updatedConnection.connectionErrorReason = undefined;
    } else {
      notify(`Connection failed due to error: ${testResult.errorType} ${testResult.errorMessage}`, NotificationType.Error);
      updatedConnection.connectionStatus = ConnectionStatus.Error;
      updatedConnection.connectionErrorReason = testResult.errorMessage;
    }
    setCachedConnection(updatedConnection);
    return testResult.isSuccessful;
  };

  const handleConditionsChanged = (selections: MultiValue<{ value: string, label: string }>) => {
    const updatedConnection = { ...cachedConnection };
    const updatedConditions: DocumentStatusKey[] = selections.map(selection => DocumentStatusKey[selection.value as keyof typeof DocumentStatusKey]);
    updatedConnection.conditions = updatedConditions;
    setCachedConnection(updatedConnection);
    setConditions(updatedConnection.conditions.map(c => ({ value: c as string, label: c as string })))
  };

  if (
    (id !== newEntryId && !loadedConnection.id) ||
    !selectedProjectId
  ) {
    return <div className="py-5 mb-5 border-bottom container">
      Loading connection information...
    </div>;
  }

  const heading: NavHeadingProps = {
    title: id === newEntryId ? 'Create New Webhook' : 'Edit Webhook',
    ...NavHeadings.Webhooks,
  };

  return (
    <div className="py-5 mb-5 border-bottom container">
      <NavHeading {...heading} />
      <Form>
        <Row>
          <Form.Group className="mb-3 col-md-6">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={cachedConnection.name}
              onChange={onDocumentFieldChange}
              aria-label="name"
              name="name"
              tabIndex={1}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3 col-md-6">
            <Form.Label>Project</Form.Label>
            <Form.Select
              aria-label="project"
              onChange={onSelectionChange}
              value={cachedConnection.projectId}
              name="projectId"
              tabIndex={2}
            >
              {projects.map((s) => (
                <option value={s.id} key={s.id}>
                  {s.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Row>
        <Form.Group className="mb-3 col-md-12">
          <Form.Label>URL</Form.Label>
          <Form.Control
            type="url"
            value={cachedConnection.apiUrl}
            onChange={onDocumentFieldChange}
            aria-label="apiUrl"
            name="apiUrl"
            tabIndex={3}
            required
          />
        </Form.Group>
        <Form.Group className="mb-3 col-md-12">
          <Form.Label>Authorization Header Value</Form.Label>
          <Form.Control
            type="url"
            value={cachedConnection.apiKey ?? ''}
            onChange={onDocumentFieldChange}
            aria-label="apiKey"
            name="apiKey"
            tabIndex={4}
          />
          <Form.Text id="apiKeyHelp" muted>
            Enter the exact value which will be placed in the `Authorization` header.  For example, if this will be a JWT token, enter:
            <code>Bearer ey...</code>
          </Form.Text>
        </Form.Group>
        <Form.Group className="mb-3 col-md-12">
          <Form.Label>Conditions</Form.Label>
          <Select
            options={options}
            value={conditions}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            menuPortalTarget={document.body}
            menuPosition='fixed'
            className="basic-multi-select"
            classNamePrefix="select"
            name="conditions-selector"
            onChange={(selections: MultiValue<{ value: string, label: string }>) => handleConditionsChanged(selections)}
            isMulti
            closeMenuOnSelect={false}
            tabIndex={5}
            data-lpignore="true"
          />
          <Form.Text id="conditionsHelp" muted>
            Select all document states that should call this webhook.
          </Form.Text>
        </Form.Group>

        <Row className="fixed-bottom approval-bar">
          <Col md={12} className="btn-sticky-top pull-right">
            <div className="action-buttons">
              <Link
                to="/integrations"
                className="btn btn-light mb-2 mx-2"
                title="Return to integrations list">
                Cancel
              </Link>
              <Button
                type="button"
                onClick={() => { testConnection() }}
                // variant="info"
                className="btn btn-secondary mb-2 mx-2"
                title="Save and test connection with dummy data"
                tabIndex={6}
              >
                <i className="bi bi-send-fill px-2 xxs-hide" />
                Save and Test Connection
              </Button>
              <Button
                type="button"
                onClick={async () => {
                  const result = await onSave();
                  if (result.hasSavedSuccessfully) {
                    logAndNotifyConnectionSaved(result.connection);
                    setCachedConnection(result.connection);
                  } else {
                    logAndNotifyConnectionSaveFailure(result.connection);
                  }
                }}
                className=" btn btn-success mb-2 mx-2"
                tabIndex={7}
                title='Save connection information'
              >
                <i className="bi bi-journal-check px-2 xxs-hide" />
                Save
              </Button>
            </div>
          </Col>
        </Row>
      </Form>

    </div>
  );
}

export default EditWebhook;
